/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  toast
} from '@etherisc/ui-kit';
import { Plus, CreditCard, TrendingUp } from 'lucide-react';
import { InsurancePremiumForm } from '../../components/insurance/InsurancePremiumForm';
import { PaymentReceivedForm } from '../../components/insurance/PaymentReceivedForm';
import { PremiumEarningForm } from '../../components/insurance/PremiumEarningForm';
import { Modal } from '../../components/ledger/Modal';
import { PermissionGate } from '../../components/PermissionGate';

export function PremiumManagement() {
  const [activeTab, setActiveTab] = useState<'premium-due' | 'payment-received' | 'premium-earning'>('premium-due');
  const [showForm, setShowForm] = useState(false);

  const handleFormSuccess = (journal: any) => {
    toast.success(`Transaction recorded successfully: ${journal.externalRef}`);
    setShowForm(false);
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  const getFormTitle = () => {
    switch (activeTab) {
      case 'premium-due':
        return 'Record Premium Due';
      case 'payment-received':
        return 'Record Payment Received';
      case 'premium-earning':
        return 'Record Premium Earning';
      default:
        return 'Insurance Transaction';
    }
  };

  const getFormComponent = () => {
    switch (activeTab) {
      case 'premium-due':
        return <InsurancePremiumForm onSuccess={handleFormSuccess} onCancel={handleFormCancel} />;
      case 'payment-received':
        return <PaymentReceivedForm onSuccess={handleFormSuccess} onCancel={handleFormCancel} />;
      case 'premium-earning':
        return <PremiumEarningForm onSuccess={handleFormSuccess} onCancel={handleFormCancel} />;
      default:
        return null;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Premium Management</h1>
          <p className="text-muted-foreground">
            Manage insurance premium lifecycle - due, payments, and earnings
          </p>
        </div>
        
        <PermissionGate permission="ledger:write">
          <Button onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Transaction
          </Button>
        </PermissionGate>
      </div>

      {/* Premium Lifecycle Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Premium Lifecycle Management</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="premium-due" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Premium Due
              </TabsTrigger>
              <TabsTrigger value="payment-received" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Payment Received
              </TabsTrigger>
              <TabsTrigger value="premium-earning" className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Premium Earning
              </TabsTrigger>
            </TabsList>

            <TabsContent value="premium-due" className="mt-6">
              <div className="space-y-4">
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Record Premium Due</h3>
                  <p className="text-muted-foreground mb-4">
                    Create journal entries when premiums become due from clients
                  </p>
                  <PermissionGate permission="ledger:write">
                    <Button onClick={() => setShowForm(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Record Premium Due
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="payment-received" className="mt-6">
              <div className="space-y-4">
                <div className="text-center py-8">
                  <CreditCard className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Record Payment Received</h3>
                  <p className="text-muted-foreground mb-4">
                    Record payments received from clients via payment providers
                  </p>
                  <PermissionGate permission="ledger:write">
                    <Button onClick={() => setShowForm(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Record Payment Received
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="premium-earning" className="mt-6">
              <div className="space-y-4">
                <div className="text-center py-8">
                  <TrendingUp className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Record Premium Earning</h3>
                  <p className="text-muted-foreground mb-4">
                    Convert unearned premium liability to premium revenue
                  </p>
                  <PermissionGate permission="ledger:write">
                    <Button onClick={() => setShowForm(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Record Premium Earning
                    </Button>
                  </PermissionGate>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Transaction Form Modal */}
      <Modal
        isOpen={showForm}
        onClose={handleFormCancel}
        title={getFormTitle()}
      >
        {getFormComponent()}
      </Modal>
    </div>
  );
}
